import * as React from 'react';
import { Translate } from '../../../../../shared/LanguageService';
import { IOrderRow, IOrderType, OrderTypes, IProduct, PhotoOrPlan } from '../Types';
import { Checkbox, Form, FormGroup, FormControl, ControlLabel, HelpBlock } from 'react-bootstrap';
import FormSelect from '../../../../../components/common/FormSelect';
import ValidatableFormGroup from '../../../../../components/common/ValidatableFormGroup';

interface IProps {
    orderType: OrderTypes;
    errors: any;
    orderRow: IOrderRow;
    onChange: (e: any) => void;
    onPhotographerCompensationSetManuallyChange: (e: any) => void;
    possibleProducts: any;
    hasKeyProductSelected: boolean;
}

const OrderRowForm = (props: IProps) => {
    function errorExists(errors: any, key: string) {
        if (errors && errors[key]) {
            return true;
        }

        return false;
    }

    return (
        <Form>
            {props.possibleProducts !== null && (
                <FormGroup>
                    <ControlLabel>
                        {Translate('view-order/basic-order-info/products/selector/label')}
                    </ControlLabel>
                    <FormSelect
                        name="product"
                        value={props.orderRow.productId as any}
                        onChange={props.onChange}
                        defaultTranslateKey="view-order/basic-order-info/products/selector/placeholder"
                    >
                        {props.possibleProducts.map((r: IProduct) => {
                            return r.name != r.standardProductName ? (
                                <option key={r.id} value={r.id as any}>
                                    {r.name} ({r.standardProductName})
                                </option>
                            ) : (
                                <option key={r.id} value={r.id as any}>
                                    {r.name}
                                </option>
                            );
                        })}
                    </FormSelect>
                </FormGroup>
            )}
            {props.possibleProducts === null && (
                <FormGroup>
                    <ControlLabel>
                        {Translate('view-order/basic-order-info/modal/form/product-name')}
                    </ControlLabel>
                    <div>{props.orderRow.productName}</div>
                </FormGroup>
            )}

            <FormGroup
                controlId="orderRowProductPriceBrokerFirm"
                validationState={
                    errorExists(props.errors, 'productPriceBrokerFirm') ? 'error' : null
                }
            >
                <ControlLabel>
                    {Translate('view-order/basic-order-info/modal/form/product-price-broker-firm')}
                </ControlLabel>
                <FormControl
                    type="number"
                    name="productPriceBrokerFirm"
                    value={
                        props.orderRow.productPriceBrokerFirm != null
                            ? props.orderRow.productPriceBrokerFirm.toString()
                            : ''
                    }
                    onChange={props.onChange}
                />
            </FormGroup>

            {(props.orderType & OrderTypes.bookable) !== 0 && (
                <FormGroup controlId="orderRowProductPhotographerCompensationSetManually">
                    <Checkbox
                        className=""
                        name="photographerCompensationSetManually"
                        checked={props.orderRow.photographerCompensationSetManually}
                        onChange={props.onPhotographerCompensationSetManuallyChange}
                    >
                        <span>
                            {Translate(
                                'view-order/basic-order-info/modal/form/set-photographer-compensation-manually'
                            )}
                        </span>
                    </Checkbox>
                </FormGroup>
            )}

            {(props.orderType & OrderTypes.bookable) !== 0 && (
                <FormGroup
                    controlId="orderRowProductPhotographerCompensation"
                    validationState={
                        errorExists(props.errors, 'photographerCompensation') ? 'error' : null
                    }
                >
                    <ControlLabel>
                        {Translate(
                            'view-order/basic-order-info/modal/form/photographer-compensation'
                        )}
                    </ControlLabel>
                    <FormControl
                        type="number"
                        disabled={!props.orderRow.photographerCompensationSetManually}
                        name="photographerCompensation"
                        value={
                            props.orderRow.photographerCompensation != null
                                ? props.orderRow.photographerCompensation.toString()
                                : ''
                        }
                        onChange={props.onChange}
                    />
                </FormGroup>
            )}

            <FormGroup
                controlId="orderRowProductPricePropertyOwner"
                validationState={
                    errorExists(props.errors, 'productPricePropertyOwner') ? 'error' : null
                }
            >
                <ControlLabel>
                    {Translate(
                        'view-order/basic-order-info/modal/form/product-price-property-owner'
                    )}
                </ControlLabel>
                <FormControl
                    type="number"
                    name="productPricePropertyOwner"
                    value={
                        props.orderRow.productPricePropertyOwner != null
                            ? props.orderRow.productPricePropertyOwner.toString()
                            : ''
                    }
                    onChange={props.onChange}
                />
            </FormGroup>

            <FormGroup
                controlId="orderRowQuantity"
                validationState={errorExists(props.errors, 'quantity') ? 'error' : null}
            >
                <ControlLabel>
                    {Translate('view-order/basic-order-info/modal/form/quantity')}
                </ControlLabel>
                <FormControl
                    type="number"
                    name="quantity"
                    value={
                        props.orderRow.quantity != null ? props.orderRow.quantity.toString() : ''
                    }
                    onChange={props.onChange}
                />
                {props.errors && <HelpBlock>{props.errors.quantity}</HelpBlock>}
            </FormGroup>

            <FormGroup controlId="orderRowInvoiceComment">
                <ControlLabel>
                    {Translate('view-order/basic-order-info/modal/form/invoice-comment')}
                </ControlLabel>
                <FormControl
                    type="text"
                    name="invoiceComment"
                    value={props.orderRow.invoiceComment ? props.orderRow.invoiceComment : ''}
                    onChange={props.onChange}
                />
            </FormGroup>

            <FormGroup controlId="orderRowPhotographerComment">
                <ControlLabel>
                    {Translate('view-order/basic-order-info/modal/form/photographer-comment')}
                </ControlLabel>
                <FormControl
                    type="text"
                    name="photographerComment"
                    value={
                        props.orderRow.photographerComment ? props.orderRow.photographerComment : ''
                    }
                    onChange={props.onChange}
                />
            </FormGroup>

            {props.hasKeyProductSelected && (
                <>
                    <FormGroup>
                        <ControlLabel>
                            {Translate('orders/create/form/input-label/key-pickup-address')}
                        </ControlLabel>
                    </FormGroup>
                    <FormGroup
                        controlId="keyPickupAddress"
                        validationState={
                            errorExists(props.errors, 'keyPickupAddress') ? 'error' : null
                        }
                    >
                        <div style={{ marginBottom: '6px' }}>
                            <Checkbox
                                className="inline"
                                name="useBrokerFirmAddress"
                                checked={Boolean((props.orderRow as any).useBrokerFirmAddress)}
                                onChange={props.onChange}
                            >
                                <span>
                                    {Translate(
                                        'orders/create/form/input-label/use-broker-firm-address'
                                    )}
                                </span>
                            </Checkbox>
                        </div>
                        {props.errors && props.errors.keyPickupAddress && (
                            <HelpBlock>{props.errors.keyPickupAddress}</HelpBlock>
                        )}
                    </FormGroup>
                    <ValidatableFormGroup
                        required
                        validationKey="keyPickupAddress"
                        errors={props.errors}
                    >
                        <div style={{ marginBottom: '6px' }}>
                            <ControlLabel>
                                {Translate(
                                    'view-order/basic-order-info/modal/form/key-pickup-street'
                                )}
                            </ControlLabel>
                            <FormControl
                                type="text"
                                name="keyPickupAddress"
                                value={(props.orderRow as any).keyPickupAddress || ''}
                                onChange={props.onChange}
                                disabled={Boolean((props.orderRow as any).useBrokerFirmAddress)}
                            />
                        </div>
                    </ValidatableFormGroup>
                    <ValidatableFormGroup
                        required
                        validationKey="keyPickupZip"
                        errors={props.errors}
                    >
                        <div style={{ marginBottom: '6px' }}>
                            <ControlLabel>
                                {Translate('view-order/basic-order-info/modal/form/key-pickup-zip')}
                            </ControlLabel>
                            <FormControl
                                type="text"
                                name="keyPickupZip"
                                value={(props.orderRow as any).keyPickupZip || ''}
                                onChange={props.onChange}
                                disabled={Boolean((props.orderRow as any).useBrokerFirmAddress)}
                            />
                        </div>
                    </ValidatableFormGroup>
                    <ValidatableFormGroup
                        required
                        validationKey="keyPickupCity"
                        errors={props.errors}
                    >
                        <div style={{ marginBottom: '6px' }}>
                            <ControlLabel>
                                {Translate(
                                    'view-order/basic-order-info/modal/form/key-pickup-city'
                                )}
                            </ControlLabel>
                            <FormControl
                                type="text"
                                name="keyPickupCity"
                                value={(props.orderRow as any).keyPickupCity || ''}
                                onChange={props.onChange}
                                disabled={Boolean((props.orderRow as any).useBrokerFirmAddress)}
                            />
                        </div>
                    </ValidatableFormGroup>
                </>
            )}
        </Form>
    );
};

export default OrderRowForm;
